import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { lowerCase } from 'lodash';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  FilterFilled,
  PlusOutlined,
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Table, message } from 'antd';
import { TableSearchFilter, TableSearchInput } from 'common/components/table-filter-component';
import LandlordModal from '~pages/dashboard/account/landlord-modal';
import ViewAccountModal from '~pages/dashboard/account/view-landlord-modal';
import * as accountActions from '~actions/account';
import {
  communicationStatus,
  sortDirectionMapping,
  platformEntity,
  entityAction,
} from '~constants';
import showElementByAuth from '~helpers/auth';
import { dayFormatType, formatDay } from '~helpers/day';
import { QUERY_LANDLORD_NAMES } from '../../landlord/queries';

const DEFAULT_ACCOUNT_LEVEL = 'landlord';

const CHECKED_COLOR = '#38b2a6';
const UNCHECKED_COLOR = '#979797';

const LandlordManagement = ({ t, landlordSlugs, history }) => {
  const dispatch = useDispatch();

  const listStatus = useSelector(
    state => state.dashboard.account?.toJS()?.communication.landlordList.status,
  );
  const users = useSelector(state => state.dashboard.account?.toJS()?.landlordList.payload);
  const total = useSelector(state => state.dashboard.account?.toJS()?.landlordList.total);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [accountLevel, setAccountLevel] = useState(null);
  const [loadingGetLandlordContactProperty, setLoadingGetLandlordContactProperty] = useState(true);
  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 10,
    email: null,
    phone: null,
    sortDirection: sortDirectionMapping.descend,
    landlordSlugs: landlordSlugs || null,
  });
  const [accountEmailVisible, setAccountEmailVisible] = useState(false);
  const [accountNameVisible, setAccountNameVisible] = useState(false);
  const [accountPhoneVisible, setAccountPhoneVisible] = useState(false);
  const [, setTableHeight] = useState(0);
  const [landlordNamesPage, setLandlordNamesPage] = useState(1);
  const [landlordNames, setLandlordNames] = useState([]);
  const [query, setQuery] = useState('');

  const hasMore = useRef(true);
  const landlordManagementRef = useRef(null);
  const accountNameRef = useRef(null);

  const landlordNamePageSize = 20;

  useEffect(() => {
    fetchLandlordContacts(filters);

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);

    return () => {
      window.removeEventListener('resize', updateTableHeight);
    };
  }, [filters]);

  const { loading: isLandlordNamesLoading, data: landlordNamesData } = useQuery(
    QUERY_LANDLORD_NAMES,
    {
      variables: {
        query,
        pageSize: landlordNamePageSize,
        pageNumber: landlordNamesPage,
      },
      fetchPolicy: 'network-only',
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  useEffect(() => {
    if (!isLandlordNamesLoading) {
      const result = landlordNamesData?.search?.edges?.map(item => item.node) || [];
      if (result.length < landlordNamePageSize) {
        hasMore.current = false;
      } else {
        hasMore.current = true;
      }
      if (result.length) {
        setLandlordNames(prev => [...prev, ...result]);
      }
    }
  }, [landlordNamesData]);

  const fetchLandlordContacts = (params, onSuccess) => {
    dispatch(accountActions.getLandlordContacts(params, onSuccess));
  };

  const fetchLandlordContactProperty = (params, onSuccess) => {
    dispatch(accountActions.getLandlordContactProperty(params, onSuccess));
  };

  const updateTableHeight = useCallback(() => {
    if (landlordManagementRef.current) {
      setTableHeight(landlordManagementRef.current.clientHeight - 190);
    }
  }, []);

  const handleInputSearch = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      pageNumber: 1,
      [field]: value?.trim() || null,
    }));
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const sortDirection =
      sorter.order === 'ascend' ? sortDirectionMapping.ascend : sortDirectionMapping.descend;
    setFilters(prevFilters => ({
      ...prevFilters,
      sortDirection,
      enabled: filters.enabled?.length ? filters.enabled[0] === 'true' : null,
      liveChatEnabled: filters.liveChatEnabled?.length
        ? filters.liveChatEnabled[0] === 'true'
        : null,
    }));
  };

  const handlePaginationChange = pageNumber => {
    if (pageNumber !== filters.pageNumber) {
      setFilters(prevFilters => ({
        ...prevFilters,
        pageNumber,
      }));
    }
  };

  const handlePageSizeChange = (current, pageSize) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      pageNumber: 1,
      pageSize,
    }));
  };

  const handleViewRecord = record => {
    setEditData(record);
    setShowViewModal(true);
  };

  const handleEditRecord = () => {
    setShowEditModal(true);
    setShowViewModal(false);
  };

  const handleLandlordContactProperty = email => {
    if (editData.accountLevel === 'LANDLORD') {
      setSelectedProperties([]);
      setLoadingGetLandlordContactProperty(false);
      setAccountLevel(lowerCase(editData.accountLevel));
    } else {
      fetchLandlordContactProperty({ email }, res => {
        const result = res?.landlordContacts?.edges?.[0]?.node;
        if (result?.properties?.edges) {
          setSelectedProperties(result.properties.edges);
          setLoadingGetLandlordContactProperty(false);
          setAccountLevel(lowerCase(result.accountLevel));
        }
      });
    }
  };

  const handleLandlordNameChange = value => {
    setFilters(prevFilters => ({ ...prevFilters, landlordSlugs: value }));
  };

  const handleLandlordNameScrollEnd = () => {
    if (!hasMore.current) return;
    setLandlordNamesPage(prevPage => prevPage + 1);
  };

  const handleLandlordNameSearch = value => {
    setLandlordNamesPage(1);
    setLandlordNames([]);
    setQuery(value);
    hasMore.current = true;
  };

  const columns = [
    {
      title: 'Account Email',
      dataIndex: 'email',
      key: 'account-email',
      width: '20%',
      filterIcon: (
        <SearchOutlined color={filters.email?.length ? CHECKED_COLOR : UNCHECKED_COLOR} />
      ),
      filterDropdown: (
        <TableSearchInput field="email" value={filters.email} onChange={handleInputSearch} />
      ),
      filterDropdownVisible: accountEmailVisible,
      onFilterDropdownVisibleChange: setAccountEmailVisible,
      render: text => (
        <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text ?? '-'}</div>
      ),
    },
    {
      title: 'Landlord Name',
      dataIndex: 'landlord',
      key: 'landlord-name',
      width: '15%',
      render: text => text?.name ?? '-',
      filterDropdown: (
        <div>
          <label ref={accountNameRef} />
          <TableSearchFilter
            active={accountNameVisible}
            targetInput={accountNameRef.current}
            defaultOption={landlordSlugs || null}
            optionValueProp="slug"
            isFetching={isLandlordNamesLoading}
            options={landlordNames}
            selectedValue={filters.landlordSlugs}
            onSearch={handleLandlordNameSearch}
            onChange={handleLandlordNameChange}
            onClose={() => setAccountNameVisible(false)}
            onScrollEnd={handleLandlordNameScrollEnd}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined color={filters.landlordSlugs?.length ? CHECKED_COLOR : UNCHECKED_COLOR} />
      ),
      filterDropdownVisible: accountNameVisible,
      onFilterDropdownVisibleChange: value => {
        setAccountNameVisible(value);
      },
    },
    {
      title: 'Account Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: '15%',
      render: text => text ?? '-',
      filterIcon: (
        <SearchOutlined color={filters.phone?.length ? CHECKED_COLOR : UNCHECKED_COLOR} />
      ),
      filterDropdown: (
        <TableSearchInput field="phone" value={filters.phone} onChange={handleInputSearch} />
      ),
      filterDropdownVisible: accountPhoneVisible,
      onFilterDropdownVisibleChange: setAccountPhoneVisible,
    },
    {
      title: 'Dashboard Access',
      dataIndex: 'enabled',
      width: '13%',
      filters: [
        { text: 'Enabled', value: 'true' },
        { text: 'Disabled', value: 'false' },
      ],
      filteredValue: typeof filters.enabled === 'boolean' ? [String(filters.enabled)] : null,
      filterMultiple: false,
      filterIcon: (
        <FilterFilled
          style={{
            color: typeof filters.enabled === 'boolean' ? '#38b2a6' : '#c8c9cb',
          }}
        />
      ),
      render: enabled => (
        <>
          <span style={{ paddingRight: 8 }}>
            {enabled ? (
              <CheckOutlined style={{ color: '#38B2A6' }} />
            ) : (
              <CloseOutlined style={{ color: '#BFBFBF' }} />
            )}
          </span>
          {enabled ? 'Enabled' : 'Disabled'}
        </>
      ),
    },
    {
      title: 'Livechat',
      dataIndex: 'liveChatEnabled',
      width: '13%',
      filters: [
        { text: 'Enabled', value: 'true' },
        { text: 'Disabled', value: 'false' },
      ],
      filteredValue:
        typeof filters.liveChatEnabled === 'boolean' ? [String(filters.liveChatEnabled)] : null,
      filterMultiple: false,
      filterIcon: (
        <FilterFilled
          style={{
            color: typeof filters.liveChatEnabled === 'boolean' ? '#38b2a6' : '#c8c9cb',
          }}
        />
      ),
      render: enabled => (
        <>
          <span style={{ paddingRight: 8 }}>
            {enabled ? (
              <CheckOutlined style={{ color: '#38B2A6' }} />
            ) : (
              <CloseOutlined style={{ color: '#BFBFBF' }} />
            )}
          </span>
          {enabled ? 'Enabled' : 'Disabled'}
        </>
      ),
    },
    {
      title: 'Updated Time',
      dataIndex: 'updatedAt',
      key: 'updated-time',
      width: '14%',
      sorter: true,
      render: text => (text ? formatDay(dayFormatType.UKFORMAT, text) : '-'),
    },
    {
      title: 'Action',
      key: 'landlord-action',
      width: '10%',
      render: (text, record) => (
        <If condition={showElementByAuth(platformEntity.IDENTITY_CMS_USERS, entityAction.UPDATE)}>
          <button
            onClick={() => handleViewRecord(record)}
            className="landlord-account-management__btn"
          >
            View
          </button>
        </If>
      ),
    },
  ];

  return (
    <div className="landlord-account-management" ref={landlordManagementRef}>
      <div className="landlord-account-management__header">
        <button
          className="landlord-account-management__btn landlord-account-management__create-new-account"
          type="button"
          onClick={() => setShowCreateModal(!showCreateModal)}
        >
          <PlusOutlined style={{ fontSize: '12px', marginLeft: '4px' }} />
          {t('cms.account.landlord_account_managing.create_new.account.btn')}
        </button>
      </div>

      <div className="landlord-account-management__content">
        <Table
          loading={listStatus === communicationStatus.FETCHING}
          style={{ height: '100%' }}
          rowKey={user => user.id}
          dataSource={users}
          columns={columns}
          pagination={{
            current: filters.pageNumber,
            pageSize: filters.pageSize,
            total,
            showSizeChanger: true,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePageSizeChange,
          }}
          onChange={handleTableChange}
        />
      </div>

      {showCreateModal && (
        <LandlordModal
          activeModal
          t={t}
          onClose={() => setShowCreateModal(false)}
          modalType="add"
          isLandlord
          properties={selectedProperties}
          accountLevel={DEFAULT_ACCOUNT_LEVEL}
        />
      )}

      {showEditModal && (
        <div className="landlord-account-management__edit-modal">
          <LandlordModal
            activeModal
            t={t}
            onClose={() => setShowEditModal(false)}
            modalType="edit"
            data={editData}
            getList={() => fetchLandlordContacts(filters)}
            history={history}
            isLandlord
            properties={selectedProperties}
            accountLevel={accountLevel}
          />
        </div>
      )}

      {showViewModal && (
        <div className="landlord-account-management__edit-modal">
          <ViewAccountModal
            activeModal
            t={t}
            onClose={() => setShowViewModal(false)}
            data={editData}
            properties={selectedProperties}
            accountLevel={accountLevel}
            onOpenEditModal={handleEditRecord}
            handleLandlordContactProperty={handleLandlordContactProperty}
            loading={loadingGetLandlordContactProperty}
          />
        </div>
      )}
    </div>
  );
};

LandlordManagement.propTypes = {
  t: PropTypes.func.isRequired,
  landlordSlugs: PropTypes.array,
  history: PropTypes.object.isRequired,
};

LandlordManagement.defaultProps = {
  landlordSlugs: null,
};

export default withTranslation()(LandlordManagement);
